import { memo, useMemo, useState } from "react";
import { Drawer, Table, Tag, Input, Radio, Typography, Spin, Space } from "antd";
import { 
  ShoppingCartOutlined, 
  PlusOutlined, 
  DeleteOutlined, 
  SwapOutlined,
  CheckCircleOutlined,
  SearchOutlined,
  DiffOutlined
} from "@ant-design/icons";
import styles from "../styles/UberSyncUI.module.css";

const { Text } = Typography;

const MenuDetailDrawer = ({ visible, menu: localMenu, liveMenu, liveMenuLoading, onClose }) => {
  const [filterType, setFilterType] = useState("changes"); // "all" | "changes" | "added" | "deleted" | "price_diff"
  const [searchText, setSearchText] = useState("");
  const [pageSize, setPageSize] = useState(15);

  // Flatten the entire catalog comparison to a tabular format
  const tableData = useMemo(() => {
    const allSkus = new Set();
    const productInfoMap = new Map();

    if (!localMenu || !liveMenu) return [];

    // Parse local products
    localMenu.forEach(cat => {
      (cat.products || []).forEach(prod => {
        allSkus.add(prod.sku);
        productInfoMap.set(prod.sku, {
          categoryName: cat.name.toUpperCase(),
          productName: prod.name,
          localPrice: prod.price,
          livePrice: null,
          status: "added" // Default to added, will check against live
        });
      });
    });

    // Parse live products and update status
    liveMenu.forEach(cat => {
      (cat.products || []).forEach(prod => {
        allSkus.add(prod.sku);
        const existing = productInfoMap.get(prod.sku);
        if (existing) {
          existing.livePrice = prod.price;
          if (Math.abs((existing.localPrice || 0) - (prod.price || 0)) > 0.01) {
            existing.status = "price_diff";
          } else {
            existing.status = "sync";
          }
        } else {
          productInfoMap.set(prod.sku, {
            categoryName: cat.name.toUpperCase(),
            productName: prod.name,
            localPrice: null,
            livePrice: prod.price,
            status: "deleted"
          });
        }
      });
    });

    return Array.from(allSkus).map(sku => ({
      key: sku,
      sku,
      ...productInfoMap.get(sku)
    }));
  }, [localMenu, liveMenu]);

  // Compute overall diff stats directly from tableData to ensure perfect consistency
  const diffStats = useMemo(() => {
    let added = 0;
    let deleted = 0;
    let priceDiff = 0;

    tableData.forEach(item => {
      if (item.status === "added") {
        added++;
      } else if (item.status === "deleted") {
        deleted++;
      } else if (item.status === "price_diff") {
        priceDiff++;
      }
    });

    return { added, deleted, priceDiff };
  }, [tableData]);

  // Filter and search logic
  const filteredData = useMemo(() => {
    return tableData.filter(item => {
      // 1. Apply Type Filter
      if (filterType === "changes" && item.status === "sync") return false;
      if (filterType === "added" && item.status !== "added") return false;
      if (filterType === "deleted" && item.status !== "deleted") return false;
      if (filterType === "price_diff" && item.status !== "price_diff") return false;

      // 2. Apply Search Filter
      if (searchText.trim() !== "") {
        const query = searchText.toLowerCase();
        const matchesName = item.productName.toLowerCase().includes(query);
        const matchesSku = item.sku.toLowerCase().includes(query);
        const matchesCategory = item.categoryName.toLowerCase().includes(query);
        return matchesName || matchesSku || matchesCategory;
      }

      return true;
    });
  }, [tableData, filterType, searchText]);
  const isSynchronized = diffStats.added === 0 && diffStats.deleted === 0 && diffStats.priceDiff === 0;

  // Get unique categories for column filters
  const categoryFilters = useMemo(() => {
    if (!tableData) return [];
    const categories = Array.from(new Set(tableData.map(item => item.categoryName))).filter(Boolean);
    categories.sort((a, b) => a.localeCompare(b));
    return categories.map(cat => ({ text: cat, value: cat }));
  }, [tableData]);

  // Table Columns config
  const columns = [
    {
      title: "Categoría",
      dataIndex: "categoryName",
      key: "categoryName",
      width: 180,
      render: (text) => <span className={styles.tableCategoryText}>{text}</span>,
      filters: categoryFilters,
      onFilter: (value, record) => record.categoryName === value,
      filterSearch: true,
    },
    {
      title: "Producto",
      dataIndex: "productName",
      key: "productName",
      render: (text, record) => (
        <div className={styles.flexColumn}>
          <span className={styles.tableProductName}>{text}</span>
          <span className={styles.skuText}>SKU: {record.sku}</span>
        </div>
      ),
      sorter: (a, b) => a.productName.localeCompare(b.productName),
    },
    {
      title: "Precio Local",
      dataIndex: "localPrice",
      key: "localPrice",
      width: 130,
      align: "right",
      render: (price, record) => {
        if (price === null) return <span className={styles.tableEmptyPrice}>-</span>;
        const priceClass = record.status === "added" ? styles.priceAdded : record.status === "price_diff" ? styles.pricePriceDiff : styles.priceNormal;
        return <span className={priceClass}>${price.toFixed(2)}</span>;
      },
      sorter: (a, b) => (a.localPrice || 0) - (b.localPrice || 0),
    },
    {
      title: "Precio Uber Eats",
      dataIndex: "livePrice",
      key: "livePrice",
      width: 140,
      align: "right",
      render: (price, record) => {
        if (price === null) return <span className={styles.tableEmptyPrice}>-</span>;
        if (record.status === "price_diff") {
          return (
            <div className={styles.priceDiffContainer}>
              <Text delete type="secondary" className={styles.oldPriceText}>${price.toFixed(2)}</Text>
              <span className={styles.newPriceText}>Nvo: ${record.localPrice.toFixed(2)}</span>
            </div>
          );
        }
        return <span className={record.status === "deleted" ? styles.priceDeleted : styles.priceNormal}>${price.toFixed(2)}</span>;
      },
      sorter: (a, b) => (a.livePrice || 0) - (b.livePrice || 0),
    },
    {
      title: "Estado / Acción",
      dataIndex: "status",
      key: "status",
      width: 160,
      align: "center",
      render: (status) => {
        switch (status) {
          case "added":
            return <Tag className={styles.flexTag} color="green" icon={<PlusOutlined />}>A AGREGAR</Tag>;
          case "deleted":
            return <Tag className={styles.flexTag} color="red" icon={<DeleteOutlined />}>A REMOVER</Tag>;
          case "price_diff":
            return <Tag className={styles.flexTag} color="orange" icon={<SwapOutlined />}>PRECIO ACT.</Tag>;
          case "sync":
            return <Tag className={styles.flexTag} color="default" icon={<CheckCircleOutlined />}>SINCRONIZADO</Tag>;
          default:
            return null;
        }
      },
      filters: [
        { text: "A Agregar", value: "added" },
        { text: "A Remover", value: "deleted" },
        { text: "Actualizar Precio", value: "price_diff" },
        { text: "Sincronizado", value: "sync" },
      ],
      onFilter: (value, record) => record.status === value,
    }
  ];

  return (
    <Drawer
      title={
        <div className={styles.drawerTitleWrapper}>
          <ShoppingCartOutlined className={styles.drawerTitleIcon} />
          <span className={styles.drawerTitleText}>Tabla Comparativa de Catálogo (Local vs. Uber)</span>
        </div>
      }
      placement="right"
      width={window.innerWidth < 768 ? "100%" : window.innerWidth < 1200 ? "90%" : 1100}
      onClose={onClose}
      visible={visible}
      destroyOnClose
      bodyStyle={{ backgroundColor: "#fafafa", padding: window.innerWidth < 768 ? "1rem" : "1.5rem", display: "flex", flexDirection: "column", overflowY: "auto" }}
    >
      {liveMenuLoading && !liveMenu ? (
        <div className={styles.spinnerContainer} style={{ marginTop: "15%" }}>
          <Spin size="large" tip="Comparando catálogo local contra Uber Eats..." />
        </div>
      ) : (
        <div className={styles.drawerContainer}>
          
          {/* Summary Dashboard Cards */}
          {!liveMenuLoading && localMenu && liveMenu && (
            <div className={styles.summaryContainer}>
              {isSynchronized ? (
                <div className={`${styles.diffSummaryCard} ${styles.diffSummaryCardSuccess}`}>
                  <div className={`${styles.diffSummaryIcon} ${styles.diffSummaryIconSuccess}`}>
                    <CheckCircleOutlined />
                  </div>
                  <div>
                    <div className={styles.diffSummaryTitle}>Sincronización</div>
                    <div className={`${styles.diffSummaryValue} ${styles.diffSummaryValueSuccess}`}>Catálogos 100% Idénticos</div>
                  </div>
                </div>
              ) : (
                <>
                  <div className={`${styles.diffSummaryCard} ${styles.diffSummaryCardSuccess}`}>
                    <div className={`${styles.diffSummaryIcon} ${styles.diffSummaryIconSuccess}`}>
                      <PlusOutlined />
                    </div>
                    <div>
                      <div className={styles.diffSummaryTitle}>Nuevos a agregar</div>
                      <div className={styles.diffSummaryValue} style={{ color: "#52c41a" }}>
                        {diffStats.added} <span className={styles.diffSummaryUnitLabel}>ítems</span>
                      </div>
                    </div>
                  </div>

                  <div className={`${styles.diffSummaryCard} ${styles.diffSummaryCardError}`}>
                    <div className={`${styles.diffSummaryIcon} ${styles.diffSummaryIconError}`}>
                      <DeleteOutlined />
                    </div>
                    <div>
                      <div className={styles.diffSummaryTitle}>A remover de Uber</div>
                      <div className={`${styles.diffSummaryValue} ${styles.diffSummaryValueError}`}>
                        {diffStats.deleted} <span className={styles.diffSummaryUnitLabel}>ítems</span>
                      </div>
                    </div>
                  </div>

                  <div className={`${styles.diffSummaryCard} ${styles.diffSummaryCardWarning}`}>
                    <div className={`${styles.diffSummaryIcon} ${styles.diffSummaryIconWarning}`}>
                      <SwapOutlined />
                    </div>
                    <div>
                      <div className={styles.diffSummaryTitle}>Precios desajustados</div>
                      <div className={`${styles.diffSummaryValue} ${styles.diffSummaryValueWarning}`}>
                        {diffStats.priceDiff} <span className={styles.diffSummaryUnitLabel}>ítems</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Filters and Search Bar Container */}
          <div className={styles.filterBar}>
            <div className={styles.filterSpace}>
              <span className={styles.filterTitle}>Mostrar:</span>
              <div className={styles.radioScrollContainer}>
                <Radio.Group 
                  value={filterType} 
                  onChange={(e) => setFilterType(e.target.value)} 
                  buttonStyle="solid"
                  size="small"
                >
                  <Radio.Button value="changes">
                    <Space size={4}><DiffOutlined /> Cambios/Alertas</Space>
                  </Radio.Button>
                  <Radio.Button value="all">Todos</Radio.Button>
                  <Radio.Button value="added">Nuevos</Radio.Button>
                  <Radio.Button value="deleted">Por Quitar</Radio.Button>
                  <Radio.Button value="price_diff">Diferencia Precio</Radio.Button>
                </Radio.Group>
              </div>
            </div>

            <Input
              placeholder="Buscar producto, SKU o categoría..."
              prefix={<SearchOutlined className={styles.searchIcon} />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className={styles.searchInput}
              allowClear
              size="small"
            />
          </div>

          {/* Interactive Table */}
          <div className={styles.tableContainer}>
            <Table
              dataSource={filteredData}
              columns={columns}
              pagination={{ 
                pageSize: pageSize, 
                showSizeChanger: true, 
                onShowSizeChange: (_current, size) => setPageSize(size),
                showTotal: (total) => `Total: ${total} productos` 
              }}
              scroll={{ x: 900, y: window.innerWidth < 768 ? undefined : "calc(100vh - 25rem)" }}
              size="middle"
              rowClassName={(record) => {
                if (record.status === "added") return styles.tableRowAddition;
                if (record.status === "deleted") return styles.tableRowDeletion;
                if (record.status === "price_diff") return styles.tableRowPriceDiff;
                return "";
              }}
            />
          </div>

        </div>
      )}
    </Drawer>
  );
};

export default memo(MenuDetailDrawer);
